import {resolveAvatarUrl} from "@buildwithflux/avatar-logic";
import Avatar, {type AvatarProps} from "@mui/material/Avatar";
import {useMemo} from "react";

import {useAvatarAdapter} from "./AvatarProvider.js";

export interface FluxAvatarProps extends Omit<AvatarProps, "src"> {
    uid: string | null;
    size?: number;
    /**
     * Pre-resolved image URL. When provided, the adapter is not consulted for
     * the picture — host apps that already hold a display-ready URL (e.g. the
     * editor's UserStorageHelper, which also handles legacy URL shapes) pass it
     * directly to avoid a redundant fetch.
     */
    pictureUrl?: string;
    displayName?: string;
    /** Render the display-name initial when no image is available. */
    showInitialFallback?: boolean;
}

export function FluxAvatar({
    uid,
    size = 32,
    alt,
    pictureUrl,
    displayName,
    showInitialFallback = true,
    sx,
    children,
    ...avatarProps
}: FluxAvatarProps) {
    const adapter = useAvatarAdapter();
    const data = adapter.useAvatarData(uid ?? null);

    const src = useMemo(() => {
        if (pictureUrl !== undefined) return pictureUrl || undefined;
        if (!data?.picture) return undefined;
        return resolveAvatarUrl(data.picture, size);
    }, [pictureUrl, data?.picture, size]);

    const name = displayName ?? data?.displayName ?? alt ?? "User";
    const content = children ?? (showInitialFallback ? name.charAt(0).toUpperCase() : undefined);

    return (
        <Avatar
            slotProps={{img: {draggable: false}}}
            {...avatarProps}
            alt={alt ?? name}
            src={src}
            sx={[{width: size, height: size, fontSize: size * 0.5}, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        >
            {content}
        </Avatar>
    );
}
